import * as React from 'react'

import { AiFeedbackDocument, AiFeedbackUpsertDocument } from 'generated/resources/graphql'

import FeedbackRequest, {
  FeedbackRequestSubmitProps,
} from 'modules/core/components/FeedbackRequest'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'

export interface Props {
  question?: string | React.ReactNode
  feedbackType: string
  resourceId: string
}

const useAiFeedbackRequest = ({ feedbackType, resourceId }: Props) => {
  const { data, loading: loadingFeedback } = useQuery(AiFeedbackDocument, {
    variables: {
      feedbackType,
      resourceId,
    },
  })
  const [saveFeedback, saveFeedbackResult] = useMutation(AiFeedbackUpsertDocument, {
    extractorFn: (data) => data.openAiMutations.upsertOpenaiFeedback,
  })

  return {
    feedback: data?.openAi.feedbacksConnection.nodes[0],
    loadingFeedback,
    saveFeedback,
    saveFeedbackResult,
  }
}

const AiFeedbackRequest = ({ question, feedbackType, resourceId }: Props) => {
  const { feedback, loadingFeedback, saveFeedback } = useAiFeedbackRequest({
    question,
    feedbackType,
    resourceId,
  })

  if (loadingFeedback) return null
  if (feedback) return null

  const onSubmit = async (submit: FeedbackRequestSubmitProps) => {
    const refetchOpts = submit.commentSubmitted
      ? {
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: AiFeedbackDocument,
              variables: {
                feedbackType,
                resourceId,
              },
            },
          ],
        }
      : {}

    const response = await saveFeedback({
      variables: {
        feedbackType,
        resourceId,
        helpful: submit.result,
        comment: submit.comment,
      },
      ...refetchOpts,
    })

    return handleResult(response, () => undefined)
  }

  return <FeedbackRequest question={question} onSubmit={onSubmit} />
}

export default AiFeedbackRequest
